import React from 'react'
import { useNavigate } from 'react-router-dom'

export default function Terms() {
  const navigate = useNavigate()

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: '40px' }}>
      <h3 style={{
        fontFamily: 'var(--font-display)', fontSize: '18px',
        color: 'var(--navy)', marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid var(--cream-dark)',
      }}>
        {title}
      </h3>
      <div style={{
        fontSize: '14px', color: 'var(--ink-soft)',
        lineHeight: 1.8,
      }}>
        {children}
      </div>
    </div>
  )

  const P = ({ children }: { children: React.ReactNode }) => (
    <p style={{ marginBottom: '12px' }}>{children}</p>
  )

  const Li = ({ children }: { children: React.ReactNode }) => (
    <li style={{ marginBottom: '8px', marginLeft: '20px' }}>{children}</li>
  )

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>

      {/* Nav */}
      <nav style={{
        background: 'var(--white)',
        borderBottom: '1px solid var(--cream-dark)',
        padding: '0 40px', height: '64px',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div
          onClick={() => navigate('/')}
          style={{
            fontFamily: 'var(--font-display)', fontSize: '22px',
            fontWeight: 700, color: 'var(--navy)', cursor: 'pointer',
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'transparent',
            border: '1px solid var(--warm-border)',
            borderRadius: 'var(--radius-full)',
            padding: '8px 20px', fontSize: '13px',
            color: 'var(--muted)', cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          ← Back
        </button>
      </nav>

      <div style={{
        maxWidth: '760px', margin: '0 auto', padding: '60px 24px',
      }}>

        <div style={{ marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(28px, 4vw, 40px)',
            color: 'var(--navy)', marginBottom: '12px',
          }}>
            Terms of Service
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
            Last updated: July 2026 · Olatunji Ventures Ltd
          </p>
          <div style={{
            marginTop: '20px', padding: '16px 20px',
            background: 'rgba(200,150,62,0.08)',
            border: '1px solid rgba(200,150,62,0.2)',
            borderRadius: 'var(--radius-md)',
            fontSize: '14px', color: 'var(--ink-soft)', lineHeight: 1.7,
          }}>
            <strong style={{ color: 'var(--navy)' }}>Important:</strong> Velumio
            is a wellbeing support tool — it is not a medical device, clinical
            service, or substitute for professional mental health support.
            If you are experiencing a mental health crisis, please contact
            a qualified professional or call 999.
          </div>
        </div>

        <Section title="1. Acceptance of Terms">
          <P>
            By creating a Velumio account you agree to these Terms of Service
            and our Privacy Policy. If you do not agree, please do not use
            Velumio. These terms are governed by the laws of England and Wales.
          </P>
        </Section>

        <Section title="2. What Velumio Is — and Is Not">
          <P>Velumio is a personal wellbeing platform that provides:</P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>A private journal for reflective writing and voice recording</Li>
            <Li>AI-generated wellbeing scores based on journal patterns</Li>
            <Li>Weekly personal letters based on your entries</Li>
            <Li>A daily energy forecast based on your patterns</Li>
            <Li>Crisis language detection with signposting to professional support</Li>
          </ul>
          <P><strong>Velumio is not:</strong></P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>A medical device or clinical diagnostic tool</Li>
            <Li>A substitute for professional mental health treatment</Li>
            <Li>A crisis intervention service</Li>
            <Li>A replacement for therapy or counselling</Li>
          </ul>
          <P>
            Wellbeing scores and burnout risk assessments are trend indicators
            based on your self-reported data. They are not clinical diagnoses
            and should not be used to make medical decisions.
          </P>
        </Section>

        <Section title="3. Your Account">
          <P>
            You must be at least 16 years old to use Velumio. You are responsible
            for maintaining the security of your account and password. You must
            notify us immediately if you suspect unauthorised access to your account.
          </P>
          <P>
            You may only create one account per person. Accounts are personal
            and may not be shared or transferred.
          </P>
        </Section>

        <Section title="4. Acceptable Use">
          <P>You agree not to use Velumio to:</P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>Upload or share content that is illegal, harmful, or abusive</Li>
            <Li>Attempt to gain unauthorised access to the platform or other accounts</Li>
            <Li>Use automated tools to scrape or extract data</Li>
            <Li>Impersonate another person or organisation</Li>
            <Li>Use the platform for commercial purposes without our consent</Li>
          </ul>
        </Section>

        <Section title="5. Your Content">
          <P>
            You own all content you create on Velumio — your journal entries,
            voice recordings, and profile information. By using Velumio, you
            grant us a limited licence to process your content for the sole
            purpose of providing the service.
          </P>
          <P>
            We do not sell your content or use it to train AI models without
            your separate explicit consent.
          </P>
        </Section>

        <Section title="6. Subscription and Payment">
          <P>
            Velumio offers a free tier and a Pro subscription at £9.99 per month.
            Pro subscriptions are billed monthly and can be cancelled at any time.
            Cancellation takes effect at the end of the current billing period.
          </P>
          <P>
            Refunds are handled in accordance with UK consumer law. If you
            experience a billing issue, contact us at hello@velumio.co.uk.
          </P>
        </Section>

        <Section title="7. Crisis Support">
          <P>
            Velumio includes automated crisis language detection. If crisis
            language is detected in your journal entries, you will be shown
            signposting to appropriate support resources.
          </P>
          <P>
            This detection is not perfect and should not be relied upon as
            a safety net. If you are in crisis, please contact:
          </P>
          <ul style={{ marginBottom: '12px' }}>
            <Li><strong>Samaritans:</strong> 116 123 (free, 24/7)</Li>
            <Li><strong>Crisis text line:</strong> Text SHOUT to 85258</Li>
            <Li><strong>Emergency services:</strong> 999</Li>
          </ul>
        </Section>

        <Section title="8. B2B and Organisational Use">
          <P>
            When Velumio is used within an organisation, HR administrators
            have access to anonymised aggregate data only. No individual
            employee names, scores, or journal content are ever visible to
            administrators. A minimum of 5 team members must have scores
            before any aggregate data is shown.
          </P>
        </Section>

        <Section title="9. Limitation of Liability">
          <P>
            To the maximum extent permitted by law, Olatunji Ventures Ltd
            is not liable for any indirect, incidental, or consequential
            damages arising from your use of Velumio. Our total liability
            to you shall not exceed the amount you paid us in the 12 months
            preceding the claim.
          </P>
        </Section>

        <Section title="10. Changes to the Service">
          <P>
            We may modify or discontinue Velumio at any time. We will provide
            reasonable notice of significant changes. If you disagree with
            changes to these terms, you may close your account.
          </P>
        </Section>

        <Section title="11. Contact">
          <P>
            For any questions about these terms, contact us at{' '}
            <strong>hello@velumio.co.uk</strong>.
          </P>
        </Section>

        <div style={{
          marginTop: '60px', paddingTop: '24px',
          borderTop: '1px solid var(--cream-dark)',
          display: 'flex', justifyContent: 'space-between',
          flexWrap: 'wrap' as const, gap: '12px',
          fontSize: '13px', color: 'var(--muted)',
        }}>
          <span>Olatunji Ventures Ltd · velumio.co.uk</span>
          <span>Last updated July 2026</span>
        </div>

      </div>
    </div>
  )
}